import isEmpty from 'lodash/isEmpty';
import { PhxTagContrast, PhxTagSize, PhxTagType } from 'phx-core';
import { IDatesTooltip, IMenuItem, ISelectedRange, PHX_DATE_FORMAT } from 'phx-core/lib/date-picker/utils/types';

import { DateRangePickerTab } from '@date-time/date-range-picker.component';
import { SUMMARY_TYPE } from '@misc/table-summary/table-summary.component';
import { IReportSwitchOption } from '@reports/report-switch.component';
import { IToggleSelectOptions } from '@misc/filter/toggle-select.component';
import { IViewAsOption } from './filters/filter-components/view-as-filter.component';

import { IClassConfig } from '@reports/report/table/directives/apply-class.directive';
import { IStyleConfig } from '@reports/report/table/directives/apply-style.directive';

import { IFormatConfig } from '@pipes/format-data.pipe';

import { IOption } from '@misc/filter/filters.service';

import { IDateRange, ISort } from '@lib/state-manager';
import { SUPPORTED_LANGUAGES } from '@lib/supported-languages';
import { PaginationType } from '@lib/utils';
import { IMoment } from '@lib/moment';
import { TABLEAU_FILTER_TYPE } from '@reports/tableau/tableau-server-api';

export enum FILTER_DATA_TYPE_SERVER {
  MULTI_SELECT = 'multi_select',
  NESTED_MULTI_SELECT = 'nested_multi_select',
  SINGLE_SELECT = 'single_select',
  DATE_RANGE_PICKER = 'date-range-picker',
  DATE_RANGE_PICKER_WITH_TYPE_SELECT = 'date-range-picker-with-type-select',
  SINGLE_DATE_PICKER = 'single-date-picker',
  TOGGLE_SELECT = 'toggle_select',
  GEOFENCE_MULTI_SELECT = 'geofence_multi_select',
  FILTER_GROUP_CONDITION = 'filter_group_condition',
  SEARCH = 'search',
  SORT = 'sort',
  PAGINATION = 'pagination',
  FORMAT = 'format',
  VEHICLE_TYPES = 'vehicle_types',
  ENTITY_TYPEAHEAD = 'entity_typeahead',
  DRIVER_TYPEAHEAD = 'driver_typeahead',
  VEHICLE_TYPEAHEAD = 'vehicle_typeahead',
  ASSET_TYPEAHEAD = 'asset_typeahead',
  ENTITY_SELECT_FILTER = 'entity_select_filter',
  DRIVER_DISPATCHER_TYPEAHEAD = 'driver_dispatcher_typeahead',
  VEHICLE_ASSET_SINGLE_SELECT = 'vehicle_asset_single_select',
}

export enum FILTER_DATA_TYPE {
  MULTI_SELECT = 'multiSelect',
  NESTED_MULTI_SELECT = 'nestedMultiSelect',
  SINGLE_SELECT = 'singleSelect',
  DATE_RANGE = 'dateRange',
  DATE_RANGE_PICKER_WITH_TYPE_SELECT = 'dateRangePickerWithTypeSelect',
  DATE_RANGE_PICKER_WITH_TYPE = 'dateRangePickerWithType',// this is not a mapping of
  // FILTER_DATA_TYPE_SERVER.DATE_RANGE_PICKER_WITH_TYPE_SELECT its a special type for the date range which has select with it
  DATE_PICKER = 'datePicker',
  GROUP_MULTI_SELECT = 'groupMultiSelect',
  TOGGLE_SELECT = 'toggleSelect',
  GEOFENCE_MULTI_SELECT = 'geofenceMultiSelect',
  SEARCH = 'search',
  FILTER_GROUP_CONDITION = 'filterGroupCondition',
  SORT = 'sort',
  PAGINATION = 'pagination',
  FORMAT = 'format',
  REPORT_SWITCHER = 'reportSwitcher',
  VIEW_AS = 'viewAs',
  VEHICLE_TYPES = 'vehicleTypes',
  ENTITY_TYPEAHEAD = 'entityTypeahead',
  DRIVER_TYPEAHEAD = 'driverTypeahead',
  VEHICLE_TYPEAHEAD = 'vehicleTypeahead',
  ASSET_TYPEAHEAD = 'assetTypeahead',
  ENTITY_SELECT_FILTER = 'entitySelectFilter',
  DRIVER_DISPATCHER_TYPEAHEAD = 'driverDispatcherTypeahead',
  VEHICLE_ASSET_SINGLE_SELECT = 'vehicleAssetSingleSelect',
}

export enum FILTER_OPTION_TYPE {
  BACKEND_API_BASED = 'backendAPIBased',
  BACKEND_HARDCODED = 'backendHardcoded',
  FRONTEND_HARDCODED = 'frontendHardcoded',
}

export enum SUB_REPORT_NAME_TYPE {
  FLEET = 'fleet',
  GROUP = 'group',
  VEHICLE = 'vehicle',
  ASSET = 'asset',
  DRIVER = 'driver',
  CARD = 'card',
  CARDHOLDER = 'cardholder',
  CARDHOLDER_V2 = 'cardholder_v2',
  CATEGORY = 'category',
  FLEET_USER = 'fleet-user',
  DASHCAM = 'dashcam',
  OMNICAM = 'omnicam',
}

export enum VIEW_AS_TYPE {
  GRAPH = 'graph',
  TABLE = 'table',
}

export enum TOOLTIP_TYPE {
  ICON = 'icon',
  HOVER = 'hover',
}

export enum POPUP_DATA_TYPE {
  SUMMARY = 'summary',
  TABLE = 'table',
}

interface INoData {
  grouped_row_columns: string[];
  text: TranslationObjectType | string;
  text_align?: 'left' | 'right' | 'center';
}

interface IToggleFilterServer {
  checked_value: string | boolean;
  unchecked_value: string | boolean;
}

interface ITooltip {
  text?: TranslationObjectType | string;
  text_map?: { [key: string]: TranslationObjectType | string; };
  type: TOOLTIP_TYPE;
  placement?: 'bottom' | 'top' | 'left' | 'right';
  show_condition?: ICondition;
  tooltip_key?: string;
}

interface IOptionWithCondition extends IOption {
  show_condition_config?: ICondition;
}

export interface IConfig {
  filters: IFilterConfig[];
  summary: ISummaryConfig[];
  table: ITableConfig;
  briefText?: ISummaryConfig;
}

export interface IIconConfig {
  name?: string;
  key?: string;
  show_condition_config?: ICondition;
  tooltip_title?: string;
  tooltip_key?: string;
  phx_icon?: string;
  append_text?: boolean;
}

export interface IServerDataResponse {
  details: any[];
  total: number;
  per_page: number;
  page_no: number;
  filter_values?: any;
  [key: string]: any;
}

export type ReportParamsType = Map<string, any>;

export type TranslationObjectType = { [key in keyof typeof SUPPORTED_LANGUAGES]: string };

/* TABLE CONFIG */
export interface ITableColumn {
  title: TranslationObjectType | string;
  key: string;
  sortable?: boolean;
  sort_key?: string;
  data_type: string;
  format_type: FORMAT_TYPE;
  text_align?: 'left' | 'right' | 'center';
  grouped_hierarchy_position?: string;
  grouped_row_key?: string;
  grouped_row_summary?: IGroupedRowSummary[];
  no_data?: INoData;
  null_text?: TranslationObjectType | string;
  style_config?: IStyleConfig[];
  class_config?: IClassConfig[];
  format_config?: IFormatConfig[];
  format_config_header?: IFormatConfig[];
  icon_config?: IIconConfig;
  create_link_set?: ICreateLinkSet[];
  link_type?: 'internal' | 'external';
  show_condition_config?: ICondition;
  params_show_condition_config?: ICondition;
  header_tooltip?: ITooltip;
  tooltip?: ITooltip;
  secondary_text_config?: ISecondaryTextConfig;
  show_column?: boolean;
  all_selected_key?: string;
  all_selected_text?: TranslationObjectType | string;
  truncated_array_object_key?: string;
  truncated_array_label?: TranslationObjectType | string; // New design for truncated array
  hardcoded_text_config?: IHardCodedTextConfig;
  defect_status_config?: IDefectStatusConfig;
  is_sticky_left?: boolean;
  null_text_tooltip?: ITooltip;
  tag_config?: ITagConfig;
  stats_config?: IStatisticsConfig;
  root_key?: string;
}

interface ITagConfig {
  type: ITagType;
  size: PhxTagSize;
}

interface ITagType {
  name: {[key: string]: PhxTagType};
  contrast: PhxTagContrast;
}

export interface IHardCodedTextConfig {
  text: TranslationObjectType | string;
  show_condition_config?: ICondition;
}

interface IStatisticsConfig {
  key: string;
  format: string;
  is_opposite: boolean
  override_color: string;
  suffix: string;
}

export interface ISecondaryTextConfig {
  key?: string;
  text?: TranslationObjectType; // text is not supported when is_array is true
  show_condition_config?: ICondition;
  params_show_condition_config?: ICondition;
  is_array?: boolean;
  array_object_key?: string;
  format_config?: IFormatConfig[];
  style_config?: IStyleConfig[];
  class_config?: IClassConfig[];
  null_text?: TranslationObjectType | string;
  show_icon?: ICondition;
}

export interface IDefectStatusConfig {
  show_open_defects: boolean;
}

export interface ICreateLinkSet {
  text?: string;
  be_key?: string;
}

export interface ICondition {
  name: string;
  args?: { [key: string]: any; };
}

interface IGroupedRowSummary {
  title: TranslationObjectType | string;
  key: string;
  null_text?: TranslationObjectType | string;
  format_config?: IFormatConfig[];
}

interface INoData {
  grouped_row_columns: string[];
  text: TranslationObjectType | string;
  text_align?: 'left' | 'right' | 'center';
}

export interface ICustomMessage {
  text: TranslationObjectType | string;
  show_condition_config?: ICondition;
  dynamic_text_template?: string;
  style_config?: IStyleConfig;
  text_align?: 'left' | 'right' | 'center';
  hide_top_border?: boolean;
}

export interface IPopupMessage {
  text: TranslationObjectType | string;
  popup_type: string; // nz-message type.
  popup_data_type: POPUP_DATA_TYPE; // Evaluate data from table or summary.
  show_condition_config?: ICondition;
  duration?: number;
  icon?: string;
}

export interface ITableConfig {
  column: ITableColumn[];
  custom_message?: ICustomMessage;
  popup_config?: IPopupMessage,
  additional_empty_message?: TranslationObjectType | string;
}

export enum FORMAT_TYPE {
  TEXTUAL = 'textual',
  LINK = 'link',
  TAG = 'tag',
  PRIMARY_SECONDARY_TEXT = 'primarySecondaryText',
  LOG_GRAPH = 'logGraph',
  TRUNCATED_ARRAY = 'truncatedArray',
  POPUP_MESSAGE = 'popupMessage',
  DEFECT_TOOLTIP = 'defectTooltip',
  DEFECT_STATUS_TAG = 'defectStatusTag',
  STATISTICS = 'statistics',
  SAFETY_SCORE = 'safetyScore',
  SAFETY_SCORE_WITH_STATISTICS = 'safetyScoreWithStatistics',
  SAFETY_SCORE_RANGE = 'safetyScoreRange',
  SAFETY_EVENT_NOTES = 'safetyEventNotes',
  DEVICE_HEALTH_RECOMMENDED_ACTION = 'deviceHealthRecommendedAction',
}

export interface ITableData {
  [key: string]: any;
}

/* SUMMARY */
export interface ISummaryConfig {
  summary_type: SUMMARY_TYPE;
  data_type: string;
  title: TranslationObjectType | string;
  key: string;
  format_config?: IFormatConfig[];
  secondary_text_config?: ISecondaryTextConfig;
  null_text?: string;
  show_condition_config?: ICondition;
  params_show_condition_config?: ICondition;
  tooltip?: ITooltip;
  popup_config?: IPopupMessage,
  value?: string,
}

/* FILTER */
export enum FILTER_STATE {
  ACTIVE = 'active', // filter is displayed normally
  DISABLED = 'disabled', // filter is displayed with disable style
  HIDDEN = 'hidden', // neither shown as filter nor value is displayed in url
}

export type IFilterConfig = IDateFilterConfig | ISelectFilterConfig |
  IOtherFilterConfig | IPaginationFilterConfig | ISortFilterConfig |
  IReportSwtichFilter | IToggleSelectFilterConfig | ISearchFilterConfig |
  IFilterGroupConditionFilterConfig | IViewAsFilterConfig | IDateTypeSelectFilterConfig |
  IVehicleTypeFilterConfig | IEntityTypeAheadFilterConfig | IDriverTypeAheadFilterConfig |
  IVehicleTypeAheadFilterConfig | IAssetTypeAheadFilterConfig | IDriverDispatcherTypeAheadFilterConfig |
  IVehicleAssetSingleSelectFilterConfig;

export type DefaultDateRange = 'currentDay' | 'currentWeek' | 'currentMonth' | 'currentQuarter' | 'currentYear' |
  'defaultCycleDates' | 'lastDay' | 'lastWeek' | 'lastMonth' | 'lastQuarter' | 'lastYear' |
  'last7Days' | 'last14Days' | 'last30Days' | 'last90Days' | 'last365Days' | 'last7DaysBeforeToday' | 'last14DaysBefore3Days' | 'lastBillingCycle' | 'last14DaysBeforeToday';

  // Add these keys based on requirement (E.g. IFTA reports needs to restrict last 3 days selection)
export type DateLimitKeys = 'startOfDay' | 'startOfWeek' | 'startOfMonth' | 'startOfQuarter' | 'startOfYear' | 'last3days' | 'lastDay';

export type SubReportConfigType<T> = {
  [key in SUB_REPORT_NAME_TYPE]?: {
    filters?: T[];
    table?: T;
    summary?: T[];
    position?: number;
  };
};

export interface IDateRangeKey {
  start: string;
  end: string;
  type?: string;
}

export interface IOptionServer {
  label: TranslationObjectType | string;
  key: string;
  value?: string;
  isSelected?: boolean;
  children?: IOptionServer[];
  show_condition_config?: ICondition;
}

export interface IPhxAddonOptions {
  [key: string]: string;
}

export interface IFilterConfigServer {
  data_type: string;
  key: string;
  permission?: string;
  required: boolean;
  required_text?: TranslationObjectType | string;
  status: FILTER_STATE;
  values_endpoint?: string;
  options?: IOptionServer[];
  unshift_options?: IOptionServer[];
  placeholder?: TranslationObjectType | string;
  filter_type?: FILTER_DATA_TYPE_SERVER;
  tooltip?: TranslationObjectType | string;
  default?: IDateRange | ISelectedRange | any;
  debounce_time?: number;
  toggle_select_options?: IToggleFilterServer;
  default_tableau_internal_value?: string;
  value?: IDateRange | ISelectedRange | any;
  values?: any[];
  ignored_values?: any[];
  tableau_filter_type?: TABLEAU_FILTER_TYPE;
  search_enabled?: boolean;
  clear_selection_enabled?: boolean;
  reset_selection_enabled?: boolean;
  show_single_calendar?: boolean;
  range_limit_days?: number;
  disabled_tabs?: DateRangePickerTab[];
  default_date_range?: DefaultDateRange;
  filter_option_type?: FILTER_OPTION_TYPE;
  show_condition_config?: ICondition;
  params_show_condition_config?: ICondition;
  operator?: OperatorType;
  required_filters?: string[];
  pagination_type?: 'page_no' | 'key';
  date_format?: keyof typeof PHX_DATE_FORMAT;
  min_selectable_date?: DateLimitKeys | string;
  max_selectable_date?: DateLimitKeys | string;
  date_menu_options?: string[];
  dates_tooltip?: IDatesTooltipConfig[];
  filter_entity_type?: string;
  entities?: string[];
  vehicle_key?: string;
  asset_key?: string;
  custom_vehicle_key?: string;
  custom_asset_key?: string;
  entity_type_key?: string;
  allow_custom_search?: boolean;
  is_enter_disabled?: boolean;
  show_default_options?: boolean;
  include_deactivated_vehicles?: boolean;
  include_deactivated_assets?: boolean;
  include_deactivated_drivers?: boolean;
  include_deactivated_dispatchers?: boolean;
  only_vehicles_with_cameras?: boolean;
  show_unidentified?: boolean;
  show_subtext?: boolean;
  no_results_message?: string;
  exclude_speeding_behavior?: boolean;
}

export interface IDatesTooltipConfig {
  text: TranslationObjectType | string;
  date_range: DateLimitKeys | string;
}

export interface IFilterConfigBase {
  type: FILTER_DATA_TYPE;
  placeholder?: string;
  permission?: string;
  tooltip?: string;
  key: string | IDateRangeKey | ISort;
  required?: boolean;
  requiredText?: string;
  state?: FILTER_STATE;
  value?: IDateRange | ISelectedRange | any;
  ignoredValues?: any[];
  clearSelectionEnabled?: boolean;
  resetSelectionEnabled?: boolean;
  defaultTableauInternalValue?: string;
  tableauFilterType?: TABLEAU_FILTER_TYPE;
  showConditionConfig?: ICondition;
  paramsShowConditionConfig?: ICondition;
  showFilter?: boolean;
}

export interface IDateFilterConfig extends IFilterConfigBase {
  type: FILTER_DATA_TYPE.DATE_RANGE | FILTER_DATA_TYPE.DATE_PICKER | FILTER_DATA_TYPE.DATE_RANGE_PICKER_WITH_TYPE;
  key: IDateRangeKey;
  value?: IDateRange | ISelectedRange;
  dateRangeOptions?: {
    showSingleCalendar?: boolean;
    rangeLimitDays?: number;
    disabledTabs?: DateRangePickerTab[];
    defaultDateRange?: DefaultDateRange;
    defaultDateRangeValue?: IDateRange | ISelectedRange;
    dateFormat?: keyof typeof PHX_DATE_FORMAT;
    minSelectableDate?: Date | IMoment;
    maxSelectableDate?: Date | IMoment;
    menuOptions?: IMenuItem[];
    dynamicMenuOptions?: string[];
    datesTooltip?: IDatesTooltip[];
  };
  dateTypeConfig?: IDateTypeSelectFilterConfig;
}

export interface ISelectFilterConfig extends IFilterConfigBase {
  type: FILTER_DATA_TYPE.MULTI_SELECT |
    FILTER_DATA_TYPE.NESTED_MULTI_SELECT |
    FILTER_DATA_TYPE.SINGLE_SELECT |
    FILTER_DATA_TYPE.GROUP_MULTI_SELECT |
    FILTER_DATA_TYPE.GEOFENCE_MULTI_SELECT |
    FILTER_DATA_TYPE.ENTITY_SELECT_FILTER;
  key: string;
  value?: string | string[] | number | number[];
  defaultValue?: string | string[] | number | number[];
  searchEnabled?: boolean;
  endPoint?: string;
  // unshiftOptions are added above options that are coming from config/API/FE
  unshiftOptions?: IOptionWithCondition[]; // Only for multi and single select filter
  options?: IOptionWithCondition[];
  showSubText?: boolean;
}

export interface IDateTypeSelectFilterConfig extends IFilterConfigBase {
  type: FILTER_DATA_TYPE.DATE_RANGE_PICKER_WITH_TYPE_SELECT;
  key: string;
  value?: string | string[] | number | number[];
  defaultValue?: string | string[] | number | number[];
  options?: IPhxAddonOptions;
}

export interface IVehicleTypeFilterConfig extends IFilterConfigBase {
  type: FILTER_DATA_TYPE.VEHICLE_TYPES;
  defaultValue?: string | string[];
  key: string;
}

export interface IEntityTypeAheadFilterConfig extends IFilterConfigBase {
  key: string;
  type: FILTER_DATA_TYPE.ENTITY_TYPEAHEAD;
  placeholder?: string;
  entityType?: string;
  filterEntityType?: string;
  entities?: string[];
  vehicleKey?: string;
  assetKey?: string;
  customVehicleKey?: string;
  customAssetKey?: string;
  entityTypeKey?: string;
  allowCustomSearch?: boolean;
  isEnterDisabled?: boolean;
  showDefaultOptions?: boolean;
  includeDeactivatedVehicles?: boolean;
  includeDeactivatedAssets?: boolean;
  noResultsMessage?: string;
}

export interface IVehicleAssetSingleSelectFilterConfig extends IFilterConfigBase {
  key: string;
  type: FILTER_DATA_TYPE.VEHICLE_ASSET_SINGLE_SELECT;
  placeholder?: string;
  vehicleKey?: string;
  assetKey?: string;
  customVehicleKey?: string;
  customAssetKey?: string;
  entityTypeKey?: string;
  allowCustomSearch?: boolean;
  isEnterDisabled?: boolean;
  includeDeactivatedVehicles?: boolean;
  includeDeactivatedAssets?: boolean;
  noResultsMessage?: string;
  defaultValue?: string | string[] | number | number[];
}

export interface IDriverDispatcherTypeAheadFilterConfig extends IFilterConfigBase {
  key: string;
  type: FILTER_DATA_TYPE.DRIVER_DISPATCHER_TYPEAHEAD;
  placeholder?: string;
  filterEntityType?: string;
  entities?: string;
  showDefaultOptions?: boolean;
  includeDeactivatedDispatchers?: boolean;
  includeDeactivatedDrivers?: boolean;
  noResultsMessage?: string;
  defaultValue?: string | number;
}

export interface IDriverTypeAheadFilterConfig extends IFilterConfigBase {
  key: string;
  type: FILTER_DATA_TYPE.DRIVER_TYPEAHEAD;
  placeholder?: string;
  showUnidentified?: boolean;
  showSubtext?: boolean;
  includeDeactivatedDrivers?: boolean;
}

export interface IVehicleTypeAheadFilterConfig extends IFilterConfigBase {
  key: string;
  type: FILTER_DATA_TYPE.VEHICLE_TYPEAHEAD;
  placeholder?: string;
  showSubtext?: boolean;
  includeDeactivatedVehicles?: boolean;
}

export interface IAssetTypeAheadFilterConfig extends IFilterConfigBase {
  key: string;
  type: FILTER_DATA_TYPE.ASSET_TYPEAHEAD;
  placeholder?: string;
  showSubtext?: boolean;
  includeDeactivatedAssets?: boolean;
}

export interface ITypeaheadFilterConfigWrapper {
  config: IEntityTypeAheadFilterConfig;
  value: string | {
    id: string,
    type: string,
  };
}

export function instanceOfISelectFilterConfig(obj: any): obj is ISelectFilterConfig {
  return ('key' in obj && typeof obj.key === 'string') && ('type' in obj && [
    FILTER_DATA_TYPE.MULTI_SELECT,
    FILTER_DATA_TYPE.NESTED_MULTI_SELECT,
    FILTER_DATA_TYPE.SINGLE_SELECT,
    FILTER_DATA_TYPE.GROUP_MULTI_SELECT,
    FILTER_DATA_TYPE.GEOFENCE_MULTI_SELECT,
    FILTER_DATA_TYPE.ENTITY_SELECT_FILTER,
  ].includes(obj.type));
}

export function instanceOfIDateFilterConfig(obj: any): obj is IDateFilterConfig {
  return ('key' in obj) && ('type' in obj && [
    FILTER_DATA_TYPE.DATE_PICKER,
    FILTER_DATA_TYPE.DATE_RANGE,
    FILTER_DATA_TYPE.DATE_RANGE_PICKER_WITH_TYPE_SELECT,
    FILTER_DATA_TYPE.DATE_RANGE_PICKER_WITH_TYPE].includes(obj.type));
}

export function isInstanceOfDateTypeSelectFilter(obj: any): obj is IDateTypeSelectFilterConfig {
  return ('key' in obj) && ('type' in obj && !isEmpty(obj.options) && obj?.state === FILTER_STATE.ACTIVE &&
  FILTER_DATA_TYPE.DATE_RANGE_PICKER_WITH_TYPE_SELECT === obj.type);
}

export function instanceOfIFilterGroupConditionFilterConfig(obj: any): obj is IFilterGroupConditionFilterConfig {
  return ('type' in obj && [FILTER_DATA_TYPE.FILTER_GROUP_CONDITION].includes(obj.type)) &&
    ('operator' in obj && ['or', 'and'].includes(obj.operator))
  ;
}

export function instanceOfIPaginationFilterConfig(obj: any): obj is IPaginationFilterConfig {
  return ('key' in obj) && ('type' in obj && [FILTER_DATA_TYPE.PAGINATION].includes(obj.type));
}

export interface IOtherFilterConfig extends IFilterConfigBase {
  type: FILTER_DATA_TYPE.FORMAT;
  key: string;
  defaultValue?: number | string;
  value?: number | string;
  possibleValues?: string[];
}

export interface IPaginationFilterConfig extends IFilterConfigBase {
  type: FILTER_DATA_TYPE.PAGINATION;
  key: string;
  defaultValue?: number | string;
  value?: number | string;
  possibleValues?: string[];
  paginationType?: PaginationType;
}

export interface ISortFilterConfig extends IFilterConfigBase {
  type: FILTER_DATA_TYPE.SORT
  key: ISort;
  debounceTime?: number;
  defaultValue?: ISort;
  value?: number | string;
  possibleValues: string[];
}

export interface IReportSwtichFilter extends IFilterConfigBase {
  key: string;
  defaultValue?: unknown;
  value?: unknown;
  type: FILTER_DATA_TYPE.REPORT_SWITCHER;
  state: FILTER_STATE;
  switchOptions: IReportSwitchOption[];
}

export interface IViewAsFilterConfig extends IFilterConfigBase {
  key: string;
  defaultValue?: string;
  value?: string;
  type: FILTER_DATA_TYPE.VIEW_AS;
  state: FILTER_STATE;
  viewOptions: IViewAsOption[];
}

export interface IToggleSelectFilterConfig extends IFilterConfigBase {
  type: FILTER_DATA_TYPE.TOGGLE_SELECT;
  key: string;
  defaultValue?: number | string;
  options?: IToggleSelectOptions;
}

export interface ISearchFilterConfig extends IFilterConfigBase {
  type: FILTER_DATA_TYPE.SEARCH;
  key: string;
  defaultValue?: string;
}

export type OperatorType = 'and' | 'or';
export interface IFilterGroupConditionFilterConfig extends IFilterConfigBase {
  type: FILTER_DATA_TYPE.FILTER_GROUP_CONDITION;
  defaultValue?: string;
  key: string;
  operator: OperatorType;
  // Currently have to add all requiredFilters to the array, as only one operator ('or' or 'and') can be between all filters.
  // This only used on BE
  requiredFilters: string[];
}

